import * as echarts from 'echarts';
import moment from 'moment';
import { FONT_FAMILY, TOOLTIP_LABEL_FORMAT } from '@utils/constants';
import { adjustColor, DURATION_BUCKET_COLOR, STACK_COLOR_CODE } from './constants';
import { generateFillerData, getPlotInterval } from '@components/metrics-panel-hz/utils/format';
import humanizeUtil, { humanize } from '@utils/humanizeUtil';
import { lighten, darken } from 'polished';

// TODO: combine options and create utils

export const buildStackedOption = (chartData) => {
  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const { value } = params[0];
        const formattedDate = moment(value[0]).format(TOOLTIP_LABEL_FORMAT);
        let tooltip = `<div class="tooltip-label">${formattedDate}</div>`;
        tooltip += `<div class="tooltip-label" style="display:flex; flex-flow: row wrap; width: 300px">`;
        for (let i = params.length - 1; i >= 0; i--) {
          const { seriesName, value, marker } = params[i];
          const formattedValue = echarts.format.addCommas(value[1]);
          tooltip += `<div style="flex: 50%">${marker} ${seriesName} <strong>${formattedValue}</strong></div>`;
        }
        tooltip += `</div>`;
        return tooltip;
      },
      // confine: true,
      appendToBody: true,
      className: 'echarts-tooltip',
    },
    legend: {
      type: 'scroll',
      bottom: 0,
      lineStyle: {
        width: 0,
      },
      orient: 'horizontal',
      data: chartData?.map((i) => i.name).reverse(),
    },
    grid: {
      top: 10,
      bottom: 50,
      left: 35,
      right: 20,
    },
    xAxis: [
      {
        type: 'time',
        axisLabel: {
          formatter: (value) => {
            return moment(value).format('HH:mm');
          },
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        splitNumber: 2,
        axisLabel: {
          formatter: (value) => humanize(value),
        },
      },
    ],
    series: chartData,
    _viewType: 'timeseries',
  };
};

export const getStackedTimeSeries = ({
  data,
  groupBy,
  metric,
  interval,
  colorMap,
  series,
}: {
  data: any[];
  groupBy: string;
  metric: string;
  interval: any;
  colorMap: Record<string, string>;
  series: string[];
}) => {
  const plotInterval = getPlotInterval(interval, data?.[0]?.results);
  const fillerData = generateFillerData(plotInterval);
  const dataMap = data?.reduce((group, curr) => {
    group[curr[groupBy]] = curr.results.reduce((map, item) => {
      map[moment(item.timestamp).toISOString()] = item[metric];
      return map;
    }, {});
    return group;
  }, {});

  return series.map((type) => {
    return {
      name: type,
      type: 'line',
      stack: 'Total',
      symbol: 'none',
      areaStyle: {
        opacity: 0.8,
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            color: lighten(0.2, colorMap[type]),
          },
          {
            offset: 1,
            color: darken(0.1, colorMap[type]),
          },
        ]),
      },
      color: colorMap[type],
      data: fillerData.map(([timestamp, value]) => {
        return [timestamp, dataMap[type]?.[timestamp] ?? value];
      }),
    };
  });
};

export const getCodeColorMap = (codes: string[] = []) =>
  Object.values(
    codes.reduce(
      (map, code) => {
        let color = '#000';
        if (code && String(code).charAt(0) && STACK_COLOR_CODE[String(code).charAt(0)]) {
          color = STACK_COLOR_CODE[String(code).charAt(0)];
        }
        if (map[color]) {
          const darkerColor = adjustColor(color, map[color].length * 10);
          map[color] = [...map[color], { color: darkerColor, code }];
        } else {
          map[color] = [{ color, code }];
        }
        return map;
      },
      {} as Record<string, { code: string; color: string }[]>,
    ),
  )
    .flat()
    .reduce(
      (colorMap, curr) => {
        colorMap[curr.code] = curr.color;
        return colorMap;
      },
      {} as Record<string, string>,
    );

export const buildResponseTreeOption = (treeData) => {
  const colorMap = getCodeColorMap(treeData.map((row) => row.name));
  return {
    tooltip: {
      show: true,
      // valueFormatter: (value: number) => formatter(value, total?.[valueField]),
    },
    series: [
      {
        type: 'treemap',
        roam: false,
        nodeClick: false,
        left: 10,
        right: 10,
        bottom: 0,
        top: 5,
        name: 'network-response',
        visibleMin: 300,
        breadcrumb: {
          show: false,
        },
        itemStyle: {
          borderColor: '#eee',
        },
        label: {
          position: 'inside',
          formatter: function (params) {
            const arr = [params.name, echarts.format.addCommas(params.value)];
            return arr.join('\n');
          },
          fontSize: 14,
          fontWeight: 'bold',
        },
        data: treeData,
        levels: [
          {
            color: treeData.map((r) => colorMap[r.name]),
            itemStyle: {
              borderWidth: 0,
              gapWidth: 1,
            },
          },
        ],
      },
    ],
  };
};

export const buildDurationTreeOption = (treeData) => {
  return {
    tooltip: {
      show: true,
      // valueFormatter: (value: number) => formatter(value, total?.[valueField]),
    },
    series: [
      {
        type: 'treemap',
        roam: false,
        nodeClick: false,
        left: 10,
        right: 10,
        bottom: 0,
        top: 5,
        name: 'network-duration',
        visibleMin: 300,
        breadcrumb: {
          show: false,
        },
        itemStyle: {
          borderColor: '#eee',
        },
        label: {
          position: 'inside',
          formatter: function (params) {
            const arr = [params.name, echarts.format.addCommas(params.value)];
            return arr.join('\n');
          },
          fontSize: 14,
          fontWeight: 'bold',
        },

        data: treeData,
        levels: [
          {
            color: treeData.map((r) => DURATION_BUCKET_COLOR[r.name]),
            itemStyle: {
              borderWidth: 0,
              gapWidth: 1,
            },
          },
        ],
      },
    ],
  };
};

export const buildPercentileTreeOption = ({ treeData, colorMap, seriesName, legendMap, unit }) => {
  const tooltipFormatter = (params) => {
    const { humanizedNumber, humanizedUnit } = humanizeUtil(params.value, params.name, unit);
    return `${legendMap[params.name]}<br/>${humanizedNumber} ${humanizedUnit}`;
  };

  return {
    tooltip: {
      formatter: tooltipFormatter,
      font: FONT_FAMILY,
    },
    series: [
      {
        type: 'treemap',
        roam: false,
        nodeClick: false,
        left: 10,
        right: 10,
        bottom: 0,
        top: 5,
        name: seriesName,
        visibleMin: 300,
        breadcrumb: {
          show: false,
        },
        itemStyle: {
          borderColor: '#eee',
        },
        label: {
          position: 'inside',
          formatter: function (params) {
            const { humanizedNumber, humanizedUnit } = humanizeUtil(
              params.value,
              params.name,
              unit,
            );
            const arr = [legendMap[params.name], `${humanizedNumber} ${humanizedUnit}`];
            return arr.join('\n');
          },
          fontSize: 14,
          fontWeight: 'bold',
        },
        data: treeData,
        levels: [
          {
            color: treeData.map((r) => colorMap[r.name]),
            itemStyle: {
              borderWidth: 0,
              gapWidth: 1,
            },
          },
        ],
      },
    ],
  };
};

export const getLegendMap = (metrics) => {
  const nameMap = {};
  metrics.forEach((m) => {
    nameMap[m.name] = m.displayName.split(' ')[0];
  });
  return nameMap;
};
